import * as React from 'react';

import ResizeObserver from 'resize-observer-polyfill';

import {serializeFieldNames} from 'nutshell-core';

import type {FilterObject, CoreEntityType} from 'nutshell-core/types';

import {colors} from 'shells/colors/colors';

import {EntityListHeader} from './entity-list-header';
import {StickyScrollEntityTableWrapper} from './sticky-scroll-entity-table-wrapper';

import styles from './sticky-scroll-entity-list-container.module.css';

const ROW_HEIGHT = 41;
const LIST_HEADER_HEIGHT = 34;
const HEADER_HEIGHT = 36;

type Props = {
    filters: FilterObject[];
    defaultFields: string[];
    selectedPage: number;
    selectedSortField: string;
    selectedFields: string[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    stickyHeaderTop: number | null | undefined;
    schema: any;
    listItems: any[];
    count: number;
    entityType: CoreEntityType;
    scrollContainerSelector: string;
    tabsSelector?: string;
    isFirstLoad: boolean;
    isLoading: boolean;
    hasError: boolean;
    toggleItem?: () => void;
    includeColumnPicker?: boolean;
};

/*
 * Shared component that acts as a container for an entity list + pagination
 * controls that sits at the bottom of a layout and "fixes" the header to the top
 * of the page when the user has scrolled enough.
 */
export class StickyScrollEntityListContainer extends React.Component<Props> {
    width = 800;
    // @ts-expect-error
    resizeObserver: ResizeObserver;
    pageContentDiv: HTMLElement | null | undefined;
    horizontalScrollbar: HTMLElement | null | undefined;

    componentDidMount() {
        // @ts-expect-error
        this.pageContentDiv = document.querySelector(this.props.scrollContainerSelector);
        // @ts-expect-error
        this.horizontalScrollbar = document.querySelector(
            '.public_fixedDataTable_horizontalScrollbar'
        );

        this.resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === this.pageContentDiv) {
                    const cr = entry.contentRect;
                    if (cr.width !== this.width) {
                        this.width = cr.width;
                        // @ts-expect-error
                        this.horizontalScrollbar = document.querySelector(
                            '.public_fixedDataTable_horizontalScrollbar'
                        );
                        this.forceUpdate();
                    }
                }
            }
        });
        if (this.pageContentDiv) {
            this.resizeObserver.observe(this.pageContentDiv);
        }
    }

    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const listItemsChanged = nextProps.listItems !== this.props.listItems;

        // Scroll back to the top if a new page of content has come in, and we have a stickyHeader
        if (listItemsChanged && this.props.stickyHeaderTop) {
            const listHeaderContainer = document.querySelector(
                `.${styles['list-header-container']}`
            );

            if (this.props.tabsSelector && nextProps) {
                const tabsDiv = document.querySelector(this.props.tabsSelector);
                if (listHeaderContainer && this.pageContentDiv && tabsDiv) {
                    // @ts-expect-error
                    this.pageContentDiv.scrollTop = tabsDiv.offsetTop + tabsDiv.offsetHeight + 1;
                    // TODO: There is some wonkiness the first time we change pages and adjust the scroll top,
                    // specifically, the column headers jump up to the top of the page.  it's unclear
                    // why.  This is a lame attempt to fix it, but it doesn't quite work.
                    const columnHeaders = document.querySelector(
                        '.fixedDataTableRowLayout_rowWrapper'
                    );
                    if (columnHeaders) {
                        // @ts-expect-error
                        columnHeaders.style.top = `${
                            // @ts-expect-error
                            nextProps.stickyHeaderTop + LIST_HEADER_HEIGHT
                        }px`;
                    }
                }
            }
        }
    }

    componentDidUpdate(prevProps: Props) {
        const listHeader = document.querySelector(`.${styles['fixed-list-header-container']}`);
        if (this.props.stickyHeaderTop !== prevProps.stickyHeaderTop) {
            if (listHeader && this.props.stickyHeaderTop) {
                // @ts-expect-error
                listHeader.style.position = 'fixed';
                // @ts-expect-error
                listHeader.style.top = `${this.props.stickyHeaderTop}px`;
                // @ts-expect-error
                listHeader.style.width = `${this.width}px`;
                // @ts-expect-error
                listHeader.style.borderTop = `1px solid #${colors.offWhiteDk}`;
            } else if (listHeader) {
                // @ts-expect-error
                listHeader.style.position = 'relative';
                // @ts-expect-error
                listHeader.style.top = '0';
                // @ts-expect-error
                listHeader.style.width = '100%';
                // @ts-expect-error
                listHeader.style.borderTop = 'none';
            }

            const columnHeaders = document.querySelector('.fixedDataTableRowLayout_rowWrapper');
            if (columnHeaders && this.props.stickyHeaderTop) {
                // @ts-expect-error
                columnHeaders.style.position = 'fixed';
                // @ts-expect-error
                columnHeaders.style.top = `${this.props.stickyHeaderTop + LIST_HEADER_HEIGHT}px`;
            } else if (columnHeaders) {
                // @ts-expect-error
                columnHeaders.style.position = 'absolute';
                // @ts-expect-error
                columnHeaders.style.top = '0';
                // @ts-expect-error
                columnHeaders.style.left = '';
            }
        }

        // Update the listHeader width if the total width has changed (eg. window was resized)
        if (listHeader && listHeader.clientWidth !== this.width) {
            // @ts-expect-error
            listHeader.style.width = `${this.width}px`; // account for 16px left padding
        }
    }

    render() {
        const scrollBarHeight = this.horizontalScrollbar
            ? this.horizontalScrollbar.clientHeight
            : 0;
        const listHeight =
            ROW_HEIGHT * this.props.listItems.length + HEADER_HEIGHT + scrollBarHeight;

        const realListHeight = Math.max(listHeight, this.calcMinPeopleListHeight());

        return (
            <div className={styles['sticky-scroll-entity-list-container']}>
                <div className={styles['list-header-container']}>
                    <div className={styles['fixed-list-header-container']}>
                        <EntityListHeader
                            count={this.props.count}
                            defaultFields={this.props.defaultFields}
                            schema={this.props.schema}
                            selectedPage={this.props.selectedPage}
                            selectedFields={this.props.selectedFields}
                            onPageChange={this.handlePageChange}
                            onUpdateFields={this.handleUpdateFields}
                            includeColumnPicker={this.props.includeColumnPicker}
                            entityType={this.props.entityType}
                            isLoading={this.props.isLoading}
                        />
                    </div>
                </div>
                <StickyScrollEntityTableWrapper
                    entityType={this.props.entityType}
                    schema={this.props.schema}
                    filters={this.props.filters}
                    onParamChange={this.props.onParamChange}
                    selectedFields={this.props.selectedFields}
                    selectedPage={this.props.selectedPage}
                    selectedSortField={this.props.selectedSortField}
                    onUpdateFields={this.handleUpdateFields}
                    onSortChange={this.handleSortChange}
                    onResizeColumn={this.handleResizeColumn}
                    height={realListHeight}
                    width={this.width}
                    toggleItem={this.props.toggleItem}
                    isFirstLoad={this.props.isFirstLoad}
                    isLoading={this.props.isLoading}
                    hasError={this.props.hasError}
                    listItems={this.props.listItems}
                />
            </div>
        );
    }

    handleResizeColumn = () => {
        // @ts-expect-error
        this.horizontalScrollbar = document.querySelector(
            '.public_fixedDataTable_horizontalScrollbar'
        );
        this.forceUpdate();
    };

    handlePageChange = (page: number) => {
        this.props.onParamChange({list: {page}}, true);
    };

    handleSortChange = (sort: string) => {
        const newParams = getSortParams(sort);
        this.props.onParamChange(newParams, true);
    };

    handleUpdateFields = (fields: string[]) => {
        this.props.onParamChange(
            {list: {fields: {[this.props.entityType]: serializeFieldNames(fields)}}},
            true
        );
    };

    calcMinPeopleListHeight() {
        if (!this.pageContentDiv) return 0;
        const contentHeight = this.pageContentDiv.clientHeight;

        return contentHeight;
    }
}

/**
 * Gets params to update when applying a new sort
 * @param  {string} sort      - New sort to apply
 * @return {Object}           - New params to apply
 */
function getSortParams(sort: string) {
    return {
        list: {
            sort,
            page: 1,
        },
    };
}
